import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Stack,
    Box,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { abyssColors } from '@/theme/colors';

interface DeploymentRiskConfig {
    id: string;
    name: string;
    isSelected: boolean;
}

interface RiskConfigurationModalProps {
    open: boolean;
    onClose: () => void;
    activeDeployments: Array<{ id: string; name: string }>;
    onDeploymentsChange: (selectedDeploymentIds: string[]) => void;
    selectedDeploymentIds: string[];
}

export const RiskConfigurationModal: React.FC<RiskConfigurationModalProps> = ({
    open,
    onClose,
    activeDeployments,
    onDeploymentsChange,
    selectedDeploymentIds,
}) => {
    const [deploymentConfigs, setDeploymentConfigs] = useState<DeploymentRiskConfig[]>(() => {
        return activeDeployments.map(deployment => ({
            id: deployment.id,
            name: deployment.name,
            isSelected: selectedDeploymentIds.includes(deployment.id),
        }));
    });

    useEffect(() => {
        setDeploymentConfigs(prevConfigs => {
            const configMap = new Map(prevConfigs.map(config => [config.id, config]));
            
            return activeDeployments.map(deployment => {
                const existingConfig = configMap.get(deployment.id);
                return {
                    id: deployment.id,
                    name: deployment.name,
                    isSelected: existingConfig 
                        ? existingConfig.isSelected 
                        : selectedDeploymentIds.includes(deployment.id),
                };
            });
        });
    }, [activeDeployments, selectedDeploymentIds]);

    const handleToggleDeployment = useCallback((deploymentId: string) => {
        setDeploymentConfigs((prevConfigs) => {
            const selectedCount = prevConfigs.filter(c => c.isSelected).length;
            
            return prevConfigs.map((config) => {
                if (config.id === deploymentId) {
                    if (selectedCount <= 1 && config.isSelected) {
                        return config; // Keep it selected
                    }
                    return { ...config, isSelected: !config.isSelected };
                }
                return config;
            });
        });
    }, []);

    const handleApply = useCallback(() => {
        const selectedIds = deploymentConfigs
            .filter((config) => config.isSelected)
            .map((config) => config.id);
        onDeploymentsChange(selectedIds);
        onClose();
    }, [deploymentConfigs, onDeploymentsChange, onClose]);



    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px',
                    padding: '2rem',
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '1.8rem',
                    fontWeight: 600,
                    color: abyssColors.primary[700],
                    padding: 0,
                    mb: 3,
                }}
            >
                Risk Configuration
            </DialogTitle>

            <DialogContent sx={{ padding: 0, mb: 3 }}>
                <Typography variant="body2" color={abyssColors.primary[600]} mb={2}>
                    Select deployments to include in risk analysis:
                </Typography>



                <Stack spacing={1}>
                    {deploymentConfigs.length === 0 ? (
                        <Typography color={abyssColors.primary[500]} fontStyle="italic">
                            No deployments available
                        </Typography>
                    ) : (
                        deploymentConfigs.map((config) => (
                            <Box key={config.id} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={config.isSelected}
                                            onChange={() => handleToggleDeployment(config.id)}
                                            size="small"
                                            sx={{
                                                color: abyssColors.primary[500],
                                                '&.Mui-checked': {
                                                    color: abyssColors.primary[500],
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" color={abyssColors.primary[700]}>
                                            {config.name}
                                        </Typography>
                                    }
                                    sx={{
                                        margin: 0,
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.875rem',
                                        },
                                    }}
                                />
                            </Box>
                        ))
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ padding: 0, gap: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        color: abyssColors.primary[600],
                        borderColor: abyssColors.primary[300],
                        '&:hover': {
                            borderColor: abyssColors.primary[500],
                            backgroundColor: abyssColors.primary[50],
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    disabled={deploymentConfigs.filter(c => c.isSelected).length === 0}
                    sx={{
                        backgroundColor: abyssColors.primary[500],
                        '&:hover': {
                            backgroundColor: abyssColors.primary[600],
                        },
                        '&:disabled': {
                            backgroundColor: abyssColors.primary[200],
                            color: abyssColors.primary[400],
                        },
                    }}
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};